import { useEffect, useId, useState } from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { clampScore } from '../lib/scores';

/** Regler 0–100 (Schritt 1) plus Zahlenfeld, beide beschriftet und synchron. */
export function ScoreThreshold({
  label,
  value,
  onChange,
  disabled,
  error,
  helper,
  testId,
  direction,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
  error?: string;
  helper?: string;
  testId?: string;
  direction: 'min' | 'max';
}) {
  const id = useId();
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const commit = (raw: string) => {
    const n = Number(raw.replace(',', '.'));
    if (raw.trim() === '' || !Number.isFinite(n)) {
      setText(String(value));
      return;
    }
    const v = clampScore(n);
    setText(String(v));
    if (v !== value) onChange(v);
  };
  return (
    <Box data-testid={testId}>
      <Typography id={`${id}-label`} variant="subtitle2" component="label" htmlFor={`${id}-input`}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
        <Slider
          aria-labelledby={`${id}-label`}
          value={value}
          min={0}
          max={100}
          step={1}
          disabled={disabled}
          onChange={(_, v) => onChange(v as number)}
          valueLabelDisplay="auto"
          sx={{ flex: 1, minWidth: 120 }}
          marks={[{ value: 0, label: '0' }, { value: 50, label: '50' }, { value: 100, label: '100' }]}
          track={direction === 'min' ? 'inverted' : 'normal'}
        />
        <TextField
          id={`${id}-input`}
          value={text}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            const n = Number(e.target.value);
            if (e.target.value.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 100 && n !== value) onChange(n);
          }}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
          }}
          slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': `${label} (Zahl 0–100)`, style: { textAlign: 'right', width: 48 } } }}
          error={!!error}
          sx={{ width: 84 }}
        />
      </Box>
      {(error || helper) && (
        <Typography variant="caption" sx={{ color: error ? 'error.dark' : 'text.secondary' }} role={error ? 'alert' : undefined}>
          {error ?? helper}
        </Typography>
      )}
    </Box>
  );
}
